import { useState } from 'react';
import { OpsLayout } from '@/components/ops/OpsLayout';
import { useProcedureBundles, ProcedureBundle } from '@/hooks/useProcedureBundles';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Package,
  UserCheck,
  Stethoscope,
  FileText,
  Tag,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export default function ProcedureBundlesPage() {
  const { bundles, loading } = useProcedureBundles();

  return (
    <OpsLayout>
      <div className="p-6 space-y-6 max-w-5xl mx-auto">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="w-6 h-6 text-primary" />
            Procedure Bundles
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Dr. Larkin beta case templates — {bundles.length} bundles
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Accordion type="multiple" className="space-y-3">
            {bundles.map(bundle => (
              <AccordionItem key={bundle.id} value={bundle.id} className="border rounded-lg bg-card">
                <AccordionTrigger className="px-4 py-3 hover:no-underline">
                  <div className="flex items-center gap-3 text-left">
                    <Stethoscope className="w-4 h-4 text-primary shrink-0" />
                    <div className="flex-1">
                      <span className="font-semibold">{bundle.name}</span>
                      <div className="flex gap-2 mt-1">
                        <Badge variant="secondary" className="text-[10px]">{bundle.specialty}</Badge>
                        {bundle.assistant_expected && (
                          <Badge variant="outline" className="text-[10px] border-warning/50 text-warning">
                            <UserCheck className="w-2.5 h-2.5 mr-0.5" />
                            Asst
                          </Badge>
                        )}
                        <Badge variant="outline" className="text-[10px]">
                          {bundle.items.length} CPT{bundle.items.length !== 1 ? 's' : ''}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4 space-y-4">
                  {/* Procedures */}
                  <div>
                    <h4 className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                      <Stethoscope className="w-3 h-3" /> Included Procedures
                    </h4>
                    {bundle.items.length === 0 ? (
                      <p className="text-xs text-muted-foreground italic">
                        No procedures yet — add via admin editor
                      </p>
                    ) : (
                      <div className="space-y-1.5">
                        {bundle.items.map(item => (
                          <div
                            key={item.id}
                            className={cn(
                              'flex items-center justify-between px-3 py-2 rounded border text-sm',
                              item.is_primary
                                ? 'bg-primary/10 border-primary/20'
                                : 'bg-muted/30 border-border/50'
                            )}
                          >
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-medium">{item.procedure.cpt_code}</span>
                              <span className="text-muted-foreground text-xs">{item.procedure.name}</span>
                            </div>
                            <div className="flex gap-1.5">
                              {item.is_primary && <Badge className="bg-primary text-[10px]">Primary</Badge>}
                              {item.expected_modifier && (
                                <Badge variant="outline" className="font-mono text-[10px]">{item.expected_modifier}</Badge>
                              )}
                              {item.procedure.billable_type === 'DISPLAY_ONLY' && (
                                <Badge variant="outline" className="text-[10px] text-muted-foreground">Display</Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <Separator />

                  {/* Diagnosis Keywords */}
                  <div>
                    <h4 className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                      <Tag className="w-3 h-3" /> Diagnosis Keywords
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {bundle.diagnosis_keywords.map((kw, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{kw}</Badge>
                      ))}
                    </div>
                  </div>

                  {/* Documentation Checklist */}
                  <div>
                    <h4 className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                      <FileText className="w-3 h-3" /> Documentation Checklist
                    </h4>
                    <div className="space-y-1.5">
                      {bundle.documentation_checklist.map((item, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Checkbox disabled />
                          <span className="text-xs text-muted-foreground">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Notes */}
                  {bundle.notes && (
                    <div className="text-xs text-muted-foreground bg-muted/30 p-2 rounded">
                      {bundle.notes}
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>
    </OpsLayout>
  );
}
